/**
 * The Save and Spinoff buttons and modal for saving a project.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {IntegerField, type AnalyticsEvent} from "@khanacademy/event-schemas";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import type {GqlFetchFn} from "@khan/data-gql";
import Log, {Errors} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {getLoginUrl, redirect} from "@khan/urls";

import withGql, {
    type WithGqlProps,
    type WithoutGql,
} from "#data-access/gql/with-gql.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import type {Scratchpad} from "#scratchpad-base-package/scratchpads.ts";

import {
    mapToGqlSpinoffOriginKind,
    type SpinoffOriginKind,
} from "./queries/gql-type-mappers.ts";

const SaveModalState = {
    CLOSED: "closed" as const,
    SET_TITLE: "open" as const,
    VERIFY_PERMISSIONS: "verify" as const,
    SAVE_SCRATCHPAD: "saving" as const,
    SAVE_COMPLETE: "complete" as const,
    SAVE_ERROR: "error" as const,
    SIGN_UP_OR_LOGIN: "signup" as const,
} as const;

type Props = {
    // Is this scratchpad able to be saved using a normal "Save" button
    isSaveable: boolean;
    // Determine if a spin-off is currently allowed
    isSpinOffAllowed: () => boolean;
    // A place to register a callback for external triggering of
    // the save flow on a scratchpad
    registerTriggerSave: (
        callback: (saveSettings: boolean) => unknown,
    ) => unknown;
    // A place to register a callback for external triggering of
    // the spin-off flow on a scratchpad
    registerTriggerSpinOff: (callback: () => unknown) => unknown;
    // Handle saving the current Program (previously known as a Scratchpad)
    saveProgram: (
        gqlFetch: GqlFetchFn,
        isSpinoff: boolean,
        saveSettings: boolean,
    ) => Promise<void>;
    // The scratchpad Backbone model which is being rendered
    scratchpad: typeof Scratchpad;
    // Handle setting the title on the scratchpad
    setTitle: (title: string) => Promise<unknown>;
} & WithGqlProps &
    WithLocationProps;

type SaveState = (typeof SaveModalState)[keyof typeof SaveModalState];

type State = {
    error: string | null | undefined;
    saveModalState: SaveState;
    spinOff: boolean;
    saveSettings: boolean;
};

export function createSpinoffSubmittedAnalyticsEvent(
    scratchpadId?: string | null,
    contentKind?: SpinoffOriginKind | null,
): AnalyticsEvent | null | undefined {
    if (scratchpadId == null || contentKind == null) {
        return null;
    }

    const analyticsContentKind = mapToGqlSpinoffOriginKind(contentKind);
    if (analyticsContentKind !== "PROGRAM") {
        return {
            eventType: "ComputingLibraryProgramSpinoffSubmitted",
            eventSchemaVersion: 2,
            originContentId: scratchpadId,
            originContentKind: analyticsContentKind,
        };
    }

    const programId = parseInt(scratchpadId, 10);
    if (isNaN(programId)) {
        // We don't expect this to ever happen, but it could if the ID
        // is an empty string or there are programs in the database
        // with a non-numeric ID.
        Log.error(
            `Creating spin-off of program with non-numeric ID: '${scratchpadId}'`,
            Errors.InvalidInput,
        );
        return null;
    }

    return {
        eventType: "ComputingUserProgramSpinoffSubmitted",
        eventSchemaVersion: 2,
        originProgramId: new IntegerField(programId),
    };
}

class ScratchpadSaveSpinOffButtons extends React.Component<Props, State> {
    _isMounted: boolean;
    // @ts-expect-error - TS2564 - Property 'delayClose' has no initializer and is not definitely assigned in the constructor.
    delayClose: number;
    titleInputElement: HTMLInputElement | null | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            error: null,
            saveModalState: SaveModalState.CLOSED,
            spinOff: false,
            saveSettings: false,
        };
        // TODO(briangenisio): This is a hack to remove the deprecated call to
        // this.isMounted() but is still considered an anti-pattern.  Replace
        // with code which does not rely on the mounted state like Redux or GraphQL
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;

        // NOTE(jeresig): We're using the _isMounted anti-pattern as we
        // don't currently have a clean way to remove the event listeners :(
        this.props.registerTriggerSave((saveSettings: boolean) => {
            if (this._isMounted) {
                this.setState({spinOff: false, saveSettings}, () =>
                    this.nextModalState(),
                );
            }
        });

        this.props.registerTriggerSpinOff(() => {
            if (this._isMounted) {
                this.setState({spinOff: true}, () => this.nextModalState());
            }
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    getNextModalState(): SaveState {
        let state = this.state.saveModalState || SaveModalState.CLOSED;

        if (state === SaveModalState.CLOSED) {
            if (!PermissionsInfo.isLoggedIn) {
                const {pathname, search, hash} = this.props.location;
                const continueUrl = pathname + search + hash;
                redirect(getLoginUrl(continueUrl));
                return SaveModalState.SIGN_UP_OR_LOGIN;
            }
            state = SaveModalState.SET_TITLE;

            if (this.shouldSetTitle()) {
                return state;
            }
        }

        if (state === SaveModalState.SET_TITLE) {
            state = SaveModalState.VERIFY_PERMISSIONS;

            if (this.shouldVerifyPermissions()) {
                return state;
            }
        }

        if (state === SaveModalState.VERIFY_PERMISSIONS) {
            return SaveModalState.SAVE_SCRATCHPAD;
        }

        if (state === SaveModalState.SAVE_SCRATCHPAD) {
            return SaveModalState.SAVE_COMPLETE;
        }

        return SaveModalState.CLOSED;
    }

    shouldSetTitle(): boolean {
        const scratchpad = this.props.scratchpad;
        return this.state.spinOff || scratchpad.isNew();
    }

    shouldVerifyPermissions(): boolean {
        const scratchpad = this.props.scratchpad;
        const category = scratchpad.get("category");
        return (
            !this.state.spinOff && PermissionsInfo.hasAnyPermissions && category
        );
    }

    shouldDisplaySavingModal(): boolean {
        return this.shouldSetTitle() || this.shouldVerifyPermissions();
    }

    closeModal() {
        this.setState({
            saveModalState: SaveModalState.CLOSED,
        });
    }

    nextModalState() {
        const state = this.getNextModalState();

        this.setState({
            saveModalState: state,
        });

        // Handle the logic for states that don't require any
        // user interaction and happen automatically
        if (state === SaveModalState.SAVE_SCRATCHPAD) {
            this.props
                .saveProgram(
                    this.props.gqlFetch,
                    this.state.spinOff,
                    this.state.saveSettings,
                )
                .then(
                    () => this.nextModalState(),
                    (err) => this.handleError(err),
                );
        } else if (state === SaveModalState.SAVE_COMPLETE) {
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
            // eslint-disable-next-line no-restricted-syntax
            this.delayClose = setTimeout(() => this.closeModal(), 5000);
        }
    }

    getSuggestedTitle(): string {
        let suggestedTitle = this.props.scratchpad.get("translatedTitle");

        // Add "spin-off of" unless the title already looks like
        // 'spin-off of "xxxx".  We don't know what the
        // translation looks like, so we have to regexp-escape it.
        // "REPLACE_ME" was picked as something unlikely to be in any
        // translation of "Spin-off of"...
        const spinOffEnglish = 'Spin-off of ".*"';
        const spinOffTranslated = t`Spin-off of "${"REPLACE_ME"}"`
            .replace(/[\\^$*+?.()|[\]{}]/g, "\\$&")
            .replace("REPLACE_ME", ".*");
        const spinOffRegexp = RegExp(
            "^(" + spinOffTranslated + "|" + spinOffEnglish + ")",
            "i",
        );

        if (this.state.spinOff && !spinOffRegexp.test(suggestedTitle)) {
            suggestedTitle = t`Spin-off of "${suggestedTitle}"`;
        }

        return suggestedTitle;
    }

    renderSetTitleModal(): React.ReactElement<
        React.ComponentProps<typeof OnePaneDialog>
    > {
        const spinOff = this.state.spinOff;
        const suggestedTitle = this.getSuggestedTitle();

        const saveMessage = t`We're going to save your project now, please set a descriptive title for it:`;

        const handleSetTitle = () => {
            const newTitle = this.titleInputElement
                ? this.titleInputElement.value
                : "";
            this.props.setTitle(newTitle).then(() => this.nextModalState());

            if (spinOff) {
                // The user is creating a new spin-off, so send a CEDAR event for
                // that action.
                const {scratchpad} = this.props;
                const event = createSpinoffSubmittedAnalyticsEvent(
                    scratchpad.get("id"),
                    scratchpad.get("contentKind"),
                );
                if (event != null) {
                    publishAnalyticsEventForMinorConversion(event);
                }
            }
        };

        return (
            <OnePaneDialog
                title={t`Save As...`}
                content={
                    <View>
                        <Body tag="p">{saveMessage}</Body>
                        <input
                            ref={(node) => (this.titleInputElement = node)}
                            className={css(styles.titleInput)}
                            autoFocus
                            type="text"
                            defaultValue={suggestedTitle}
                        />
                        <Body tag="p" style={styles.findProgramsNote}>
                            {t`To find your projects later, you can:`}
                        </Body>
                        <ul className={css(styles.findProgramsList)}>
                            <li>
                                {t`Click "My Projects" from links around the site.`}
                            </li>
                            <li>
                                {t`Click your name in the upper right corner and then click ”Profile”. You'll then find the "Projects" card at the bottom right hand side of the screen.`}
                            </li>
                            {spinOff && (
                                <li>
                                    {t`To find all of your spin-offs of this project, click "Spin-offs" and look under "Your spin-offs."`}
                                </li>
                            )}
                        </ul>
                    </View>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button
                            kind="tertiary"
                            onClick={() => this.closeModal()}
                        >
                            {t`Cancel`}
                        </Button>
                        <Strut size={16} />
                        <Button onClick={handleSetTitle}>{t`Save`}</Button>
                    </View>
                }
                style={styles.dialog}
            />
        );
    }

    renderVerifyPermissionsModal(): React.ReactElement<
        React.ComponentProps<typeof OnePaneDialog>
    > {
        return (
            <OnePaneDialog
                title={t`Are you sure you want to save an official program?`}
                content={
                    <View style={[styles.modalFooter, styles.centered]}>
                        <Button
                            kind="secondary"
                            onClick={() => this.closeModal()}
                        >
                            {t`Cancel`}
                        </Button>
                        <Strut size={16} />
                        <Button onClick={() => this.nextModalState()}>
                            {t`Save`}
                        </Button>
                    </View>
                }
                style={styles.dialog}
            />
        );
    }

    renderSaveScratchpadModal(): React.ReactElement<
        React.ComponentProps<typeof OnePaneDialog>
    > {
        return (
            <OnePaneDialog
                title={t`Saving...`}
                content={
                    <View style={styles.centered}>
                        <CircularSpinner size="medium" />
                    </View>
                }
                style={styles.dialog}
            />
        );
    }

    handleError(error: string) {
        this.setState({
            saveModalState: SaveModalState.SAVE_ERROR,
            error,
        });
    }

    renderError(
        errorMessage: string,
    ): React.ReactElement<React.ComponentProps<typeof OnePaneDialog>> {
        return (
            <OnePaneDialog
                title={t`Error`}
                content={
                    <View>
                        <Body>{errorMessage}</Body>
                    </View>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button onClick={() => this.closeModal()}>
                            {t`Okay`}
                        </Button>
                    </View>
                }
                style={styles.dialog}
            />
        );
    }

    renderSaveModal(): React.ReactNode {
        const {saveModalState, error} = this.state;

        if (!error && !this.shouldDisplaySavingModal()) {
            return null;
        }

        const modal = (() => {
            if (saveModalState === SaveModalState.SET_TITLE) {
                return this.renderSetTitleModal();
            }
            if (saveModalState === SaveModalState.VERIFY_PERMISSIONS) {
                return this.renderVerifyPermissionsModal();
            }
            if (saveModalState === SaveModalState.SAVE_SCRATCHPAD) {
                return this.renderSaveScratchpadModal();
            }
            if (saveModalState === SaveModalState.SAVE_ERROR && error) {
                return this.renderError(error);
            }
            return null;
        })();

        if (!modal) {
            return null;
        }

        return (
            <ModalLauncher
                onClose={() => this.closeModal()}
                opened={!!modal}
                modal={modal}
            />
        );
    }

    render(): React.ReactNode {
        const state = this.state.saveModalState;
        const saving = state !== SaveModalState.CLOSED;
        const disabled = saving;
        let saveLabel = t`Save`;
        let spinOffLabel = t`Spin-off`;

        if (state === SaveModalState.SAVE_COMPLETE) {
            if (this.state.spinOff) {
                spinOffLabel = t`Saved!`;
            } else {
                saveLabel = t`Saved!`;
            }
        } else if (saving) {
            if (this.state.spinOff) {
                spinOffLabel = t`Spinning-off...`;
            } else {
                saveLabel = t`Saving...`;
            }
        }

        const nextModalState = (spinOff: boolean, saveSettings: boolean) => {
            this.setState({spinOff, saveSettings}, () => this.nextModalState());
        };

        const saveButton = (
            <Button
                disabled={disabled}
                onClick={() => nextModalState(false, false)}
                style={styles.toolbarButton}
            >
                {saveLabel}
            </Button>
        );

        const spinOffButton = (
            <Button
                kind={this.props.isSaveable ? "secondary" : "primary"}
                disabled={disabled}
                onClick={() => nextModalState(true, false)}
                style={styles.toolbarButton}
            >
                {spinOffLabel}
            </Button>
        );

        return (
            <View style={styles.buttonContainer}>
                {this.props.isSpinOffAllowed() && spinOffButton}
                {this.props.isSaveable && saveButton}
                {this.renderSaveModal()}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    dialog: {
        [mediaQueries.mdOrLarger]: {
            height: "auto",
        },
    },
    centered: {
        justifyContent: "center",
        placeItems: "center",
    },
    buttonContainer: {
        flexDirection: "row",
    },
    modalFooter: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    toolbarButton: {
        marginLeft: 16,
    },
    titleInput: {
        fontSize: 18,
        width: "100%",
    },
    findProgramsNote: {
        marginBottom: 10,
    },
    findProgramsList: {
        listStyle: "initial",
        marginLeft: 22,
    },
});

type ExportProps = WithoutGql<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof ScratchpadSaveSpinOffButtons,
            React.ComponentProps<typeof ScratchpadSaveSpinOffButtons>
        >
    >
>;

export default compose(
    withGql,
    withLocation,
)(ScratchpadSaveSpinOffButtons) as React.ComponentType<ExportProps>;
